/**
 * Fallback mark for chains without a bundled SVG asset — chains delivered at
 * runtime (via the per-user `chains` dataset) can predate the assets compiled
 * into this build. Renders the first letter of the chain's name over the
 * chain's brand color.
 *
 * Chain colors are global and live in the chain-config layer; pass the
 * chain's `decoration` colors — never invent a chain color. When no color is
 * available the mark falls back to a neutral grey.
 */
export type ChainAssetFallback = {
  /** The chain's `decoration.color`. */
  color?: string;
  /** The chain's `decoration.contrastColor`. */
  contrastColor?: string;
  /**
   * Display name used for the letter and the accessible label; defaults to
   * the chain key.
   */
  name?: string;
};

const NEUTRAL_COLOR = '#52525B';
const NEUTRAL_CONTRAST = '#FFFFFF';

export function ChainFallbackMark({
  fallback,
  name,
  isMonochromatic = false,
}: {
  fallback: ChainAssetFallback | undefined;
  name: string;
  isMonochromatic?: boolean;
}) {
  const color = (!isMonochromatic && fallback?.color) || NEUTRAL_COLOR;
  const contrastColor =
    (!isMonochromatic && fallback?.contrastColor) || NEUTRAL_CONTRAST;
  const letter = name.charAt(0).toUpperCase();

  return (
    <svg viewBox="0 0 32 32" aria-hidden="true">
      <rect width="32" height="32" fill={color} />
      <text
        x="16"
        y="17"
        textAnchor="middle"
        dominantBaseline="central"
        fill={contrastColor}
        fontSize="18"
        fontWeight="600"
      >
        {letter}
      </text>
    </svg>
  );
}
